import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  FileText,
  Loader2,
  TrendingUp,
} from "lucide-react";
import webAuthService from "../services/webAuthService";
import { fetchGroupMeetings } from "../services/api";
import MeetingDetails from "./MeetingDetails";

const GroupDetails = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [groupName, setGroupName] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 12,
    total_items: 0,
    total_pages: 0,
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  useEffect(() => {
    initializeUser();
  }, []);

  useEffect(() => {
    if (userId && groupId) {
      fetchGroupDetails();
      loadGroupMeetings();
    }
  }, [userId, groupId, pagination.current_page]);

  const initializeUser = async () => {
    try {
      const uid = await webAuthService.getUserId();
      if (uid) {
        setUserId(uid);
      }
    } catch (error) {
      console.error("Error initializing user:", error);
      setStatus({ type: "error", message: "Failed to initialize user" });
    }
  };

  const fetchGroupDetails = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/groups?user_id=${userId}`);
      const data = await response.json();

      if (data.status === "success") {
        const group = data.groups.find((g) => g.id === groupId);
        if (group) {
          setGroupName(group.name);
        }
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    }
  };

  const loadGroupMeetings = async () => {
    try {
      setLoading(true);
      const response = await fetchGroupMeetings({
        groupId: groupId,
        page: pagination.current_page,
        pageSize: pagination.page_size,
      });

      setMeetings(response.meetings || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching group meetings:", error);
      setStatus({ type: "error", message: "Failed to load meetings" });
    } finally {
      setLoading(false);
    }
  };

  const handleMeetingClick = (meetingId) => {
    setSelectedMeetingId(meetingId);
    setShowMeetingDetails(true);
  };

  const handleBackToGroup = () => {
    setShowMeetingDetails(false);
    setSelectedMeetingId(null);
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (showMeetingDetails) {
    return (
      <MeetingDetails
        meetingId={selectedMeetingId}
        onBack={handleBackToGroup}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F3FF" }}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/groups")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
          >
            <ArrowLeft size={20} />
            Back to Groups
          </button>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{groupName}</h1>
          <p className="text-gray-600">
            {pagination.total_items} meeting
            {pagination.total_items !== 1 ? "s" : ""} in this group
          </p>
        </div>

        {/* View Analytics Button */}
        {meetings.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => navigate(`/group-analytics/${groupId}`)}
              className="flex items-center gap-2 px-6 py-3 text-white rounded-lg transition-all font-medium shadow-sm hover:shadow-md"
              style={{ backgroundColor: "#e04665" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#e63950")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e04665")
              }
            >
              <TrendingUp className="w-5 h-5" />
              View Analytics for this Group
            </button>
          </div>
        )}

        {/* Status Message */}
        {status.message && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              status.type === "error"
                ? "bg-red-50 border-red-200 text-red-700"
                : "bg-green-50 border-green-200 text-green-700"
            }`}
          >
            {status.message}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading meetings...</p>
          </div>
        ) : meetings.length === 0 ? (
          /* Empty State */
          <div className="bg-white border border-purple-200 rounded-xl p-12 text-center shadow-sm">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: "rgba(224, 71, 101, 0.15)" }}
            >
              <FileText className="w-10 h-10 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Meetings Yet
            </h3>
            <p className="text-gray-600 mb-6">
              No meetings have been created in this group yet.
            </p>
            <button
              onClick={() => navigate("/new-meeting")}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg transition-all font-medium"
            >
              Start New Meeting
            </button>
          </div>
        ) : (
          /* Meetings Grid */
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {meetings.map((meeting) => {
                const { date, time } = formatDateTime(meeting.dateTime);
                return (
                  <div
                    key={meeting.id}
                    onClick={() => handleMeetingClick(meeting.id)}
                    className="bg-white border border-purple-200 rounded-xl p-6 hover:border-purple-400 transition-all cursor-pointer group shadow-sm"
                  >
                    {/* Meeting Title */}
                    <h3 className="text-gray-800 font-semibold text-lg mb-3 group-hover:text-purple-600 transition-colors">
                      {meeting.meeting_title || meeting.meeting_id}
                    </h3>

                    {/* Date and Time */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Calendar size={16} />
                        <span>{date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Clock size={16} />
                        <span>{time}</span>
                      </div>
                    </div>

                    {/* Summary Preview */}
                    {meeting.summary && (
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {meeting.summary}
                      </p>
                    )}

                    {/* View Details Button */}
                    <div className="mt-4 pt-4 border-t border-purple-200">
                      <span className="text-purple-600 text-sm font-medium group-hover:text-purple-700">
                        View Details →
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      current_page: Math.max(1, prev.current_page - 1),
                    }))
                  }
                  disabled={pagination.current_page === 1}
                  className="px-4 py-2 bg-white disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 border border-gray-300 rounded-lg transition-colors"
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled)
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      current_page: Math.min(
                        prev.total_pages,
                        prev.current_page + 1
                      ),
                    }))
                  }
                  disabled={pagination.current_page === pagination.total_pages}
                  className="px-4 py-2 bg-white disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 border border-gray-300 rounded-lg transition-colors"
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled)
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupDetails;
